import type { ListingAssistResultDto, SpecificationDto } from '../types';

const MAX_TAG_IMAGES = 3;
const MAX_TAG_BYTES = 5 * 1024 * 1024;

export { MAX_TAG_IMAGES, MAX_TAG_BYTES };

/** Canonical age spec name (same as backend AgeRangeParser.BabyAgeRangeSpecName). */
export const BABY_AGE_RANGE_SPEC = 'Baby Age Range (months)';

/** Normalize for fuzzy enum / key matching. */
export function normalizeAssistToken(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, '')
        .replace(/months?|maanden|mois|monate|meses|mths?|mnts?|mhts?|mos?|mnds?/g, '');
}

function parseEnumOptions(optionsCsv: string | null | undefined): string[] {
    return (optionsCsv ?? '')
        .split(',')
        .map((o) => o.trim())
        .filter(Boolean);
}

/**
 * Parse free-form age like AgeRangeParser (months only; rejects lb/kg).
 * Supports "6-12", "6-12 months", "0-1y" style bounds.
 */
export function tryParseAgeMonths(value: string): { min: number; max: number } | null {
    if (!value?.trim()) return null;
    let s = value.trim().toLowerCase();
    if (s.includes('lb') || s.includes('kg')) return null;

    s = s
        .replace(/\s*(months?|maanden|mois|monate|meses|mths?|mnts?|mos?)\s*/gi, ' ')
        .replace(/[–—]/g, '-')
        .replace(/\s+to\s+|\s+à\s+/gi, '-')
        .replace(/\s+/g, ' ')
        .trim();

    const parts = s.split('-').map((p) => p.trim()).filter(Boolean);
    if (parts.length !== 2) return null;

    const parseBound = (part: string): number | null => {
        let p = part.trim();
        if (p.endsWith('y')) {
            const y = parseInt(p.slice(0, -1).trim(), 10);
            return Number.isFinite(y) && y >= 0 ? y * 12 : null;
        }
        const n = parseInt(p, 10);
        return Number.isFinite(n) && n >= 0 ? n : null;
    };

    const min = parseBound(parts[0]);
    const max = parseBound(parts[1]);
    if (min == null || max == null || max < min) return null;
    return { min, max };
}

/** True when most options look like month buckets (0-6, 6-12, …). */
function isAgeOptionsList(options: string[]): boolean {
    if (options.length === 0) return false;
    const ageLike = options.filter((o) => tryParseAgeMonths(o) != null).length;
    return ageLike >= Math.ceil(options.length * 0.5);
}

/**
 * Snap free-form age onto a configured option only (never invents a new bucket).
 * Order: exact string → exact min–max → best fit among options that overlap
 * (max overlap months, then closest midpoint to free-form midpoint).
 */
export function matchAgeRangeOption(
    value: string,
    optionsCsv: string | null | undefined
): string | null {
    const options = parseEnumOptions(optionsCsv);
    if (!value.trim() || options.length === 0) return null;

    const raw = value.trim();
    const exact = options.find((o) => o.toLowerCase() === raw.toLowerCase());
    if (exact) return exact;

    const bounds = tryParseAgeMonths(raw);
    if (!bounds) return null;

    for (const opt of options) {
        const ob = tryParseAgeMonths(opt);
        if (ob && ob.min === bounds.min && ob.max === bounds.max) return opt;
    }

    // Best fit among overlapping configured options
    const freeMid = (bounds.min + bounds.max) / 2;
    let best: string | null = null;
    let bestOverlap = 0;
    let bestMidDist = Number.POSITIVE_INFINITY;
    let bestOptMin = Number.POSITIVE_INFINITY;

    for (const opt of options) {
        const ob = tryParseAgeMonths(opt);
        if (!ob) continue;
        const overlapStart = Math.max(bounds.min, ob.min);
        const overlapEnd = Math.min(bounds.max, ob.max);
        if (overlapStart > overlapEnd) continue;
        const overlapMonths = overlapEnd - overlapStart + 1;
        if (overlapMonths <= 0) continue;

        const optMid = (ob.min + ob.max) / 2;
        const midDist = Math.abs(optMid - freeMid);

        const better =
            overlapMonths > bestOverlap ||
            (overlapMonths === bestOverlap && midDist < bestMidDist) ||
            (overlapMonths === bestOverlap && midDist === bestMidDist && ob.min < bestOptMin);

        if (!better) continue;
        best = opt;
        bestOverlap = overlapMonths;
        bestMidDist = midDist;
        bestOptMin = ob.min;
    }

    return best;
}

/**
 * Match OCR value to an enum option string, or return null if no reasonable match.
 * Never returns a free-form value that is not in Options (OCR must not invent buckets).
 */
export function matchEnumOption(value: string, optionsCsv: string | null | undefined): string | null {
    if (!value.trim()) return null;
    const options = parseEnumOptions(optionsCsv);
    // No configured options → cannot invent a value for a closed enum
    if (options.length === 0) return null;

    // Age buckets: only exact range match (label OCR often yields 9-12mths / 6-36)
    if (isAgeOptionsList(options)) {
        return matchAgeRangeOption(value, optionsCsv);
    }

    const exact = options.find((o) => o.toLowerCase() === value.trim().toLowerCase());
    if (exact) return exact;

    const nv = normalizeAssistToken(value);
    if (!nv) return null;

    let best: string | null = null;
    let bestScore = 0;
    for (const opt of options) {
        const no = normalizeAssistToken(opt);
        if (!no) continue;
        if (no === nv) return opt;
        if (no.includes(nv) || nv.includes(no)) {
            const score = Math.min(no.length, nv.length) / Math.max(no.length, nv.length);
            if (score > bestScore) {
                bestScore = score;
                best = opt;
            }
        }
    }
    // Strict: only high-confidence substring (avoid weak partials inventing a pick)
    return bestScore >= 0.75 ? best : null;
}

export function findFieldForSpec(
    fields: Record<string, string>,
    specName: string
): string | undefined {
    if (!fields) return undefined;
    const direct = fields[specName];
    if (direct != null && String(direct).trim() !== '') return String(direct).trim();

    const nSpec = normalizeAssistToken(specName);
    for (const [k, v] of Object.entries(fields)) {
        if (!v?.trim()) continue;
        if (normalizeAssistToken(k) === nSpec) return v.trim();
        // Color / Colour
        if (
            (nSpec === 'color' || nSpec === 'colour') &&
            (normalizeAssistToken(k) === 'color' || normalizeAssistToken(k) === 'colour')
        ) {
            return v.trim();
        }
        // Material / Composition
        if (
            (nSpec === 'material' || nSpec === 'composition') &&
            (normalizeAssistToken(k) === 'material' || normalizeAssistToken(k) === 'composition')
        ) {
            return v.trim();
        }
    }
    return undefined;
}

/**
 * Apply pending assist fields into category specifications (empty slots only).
 * Enum / age: only configured options — never free-form invented values.
 */
export function applyPendingFieldsToSpecs(
    specifications: { [category: string]: { [specName: string]: string } },
    categories: string[],
    categorySpecifications: { [key: string]: SpecificationDto[] },
    pendingFields: Record<string, string>
): { [category: string]: { [specName: string]: string } } {
    if (!pendingFields || Object.keys(pendingFields).length === 0) {
        return specifications;
    }

    const next: { [category: string]: { [specName: string]: string } } = {
        ...specifications,
    };

    for (const cat of categories) {
        const specs = categorySpecifications[cat] || [];
        const catValues = { ...(next[cat] || {}) };

        for (const spec of specs) {
            const current = (catValues[spec.name] ?? '').trim();
            if (current !== '') continue;

            const raw = findFieldForSpec(pendingFields, spec.name);
            if (raw == null) continue;

            const type = String(spec.type);
            if (type === 'Boolean') {
                const lower = raw.toLowerCase();
                if (['true', 'yes', 'ja', 'oui', '1', 'y'].includes(lower)) {
                    catValues[spec.name] = 'true';
                } else if (['false', 'no', 'nee', 'non', '0', 'n'].includes(lower)) {
                    catValues[spec.name] = 'false';
                }
                continue;
            }

            if (type === 'Enum') {
                // Age (and all enums): only a real option from the schema
                const matched =
                    spec.name === BABY_AGE_RANGE_SPEC || isAgeOptionsList(parseEnumOptions(spec.options))
                        ? matchAgeRangeOption(raw, spec.options)
                        : matchEnumOption(raw, spec.options);
                if (matched) catValues[spec.name] = matched;
                continue;
            }

            // String / Integer / Decimal / Date — assign trimmed text
            catValues[spec.name] = raw;
        }

        next[cat] = catValues;
    }

    return next;
}

export function countAssistFields(result: ListingAssistResultDto | null | undefined): number {
    if (!result?.fields) return 0;
    return Object.values(result.fields).filter((v) => v && String(v).trim()).length;
}

/**
 * Extra fields that go into description when they have no matching category specification.
 * Made In is intentionally excluded (usually not needed in listing copy).
 * Brand / Size / Color stay on specs / title only.
 * Age is special: free-form OCR age that cannot map to an enum option is appended so it is not lost.
 */
export const ASSIST_DESCRIPTION_EXTRA_KEYS: { key: string; label: string }[] = [
    { key: 'Material', label: 'Material' },
    { key: 'Composition', label: 'Composition' },
    { key: 'Weight', label: 'Weight' },
    { key: 'Height', label: 'Height' },
    { key: 'Model', label: 'Model' },
    { key: 'Gender', label: 'Gender' },
];

function specExistsForField(
    fieldKey: string,
    categories: string[],
    categorySpecifications: { [key: string]: SpecificationDto[] }
): boolean {
    for (const cat of categories) {
        const specs = categorySpecifications[cat] || [];
        for (const spec of specs) {
            // Reuse findFieldForSpec matching: would this field map onto this spec name?
            const probe: Record<string, string> = { [fieldKey]: '__probe__' };
            if (findFieldForSpec(probe, spec.name) != null) return true;
        }
    }
    return false;
}

/** True when OCR age can be placed into a loaded enum age field as a real option. */
function ageMapsToLoadedEnumOption(
    ageRaw: string,
    categories: string[],
    categorySpecifications: { [key: string]: SpecificationDto[] }
): boolean {
    for (const cat of categories) {
        for (const spec of categorySpecifications[cat] || []) {
            if (String(spec.type) !== 'Enum') continue;
            const isAge =
                spec.name === BABY_AGE_RANGE_SPEC ||
                normalizeAssistToken(spec.name).includes('age') ||
                isAgeOptionsList(parseEnumOptions(spec.options));
            if (!isAge) continue;
            if (matchAgeRangeOption(ageRaw, spec.options)) return true;
        }
    }
    return false;
}

/**
 * Build listing description from OCR extras that do not map to a loaded category spec.
 * Dedupes identical Material/Composition values. Excludes Made In.
 * Unmatched free-form age (cannot invent enum buckets) is kept as "Age: …" text.
 */
export function buildAssistDescription(
    fields: Record<string, string> | null | undefined,
    categories: string[] = [],
    categorySpecifications: { [key: string]: SpecificationDto[] } = {}
): string {
    if (!fields) return '';

    const parts: string[] = [];
    const seenValues = new Set<string>();

    for (const { key, label } of ASSIST_DESCRIPTION_EXTRA_KEYS) {
        const raw = fields[key] ?? findFieldForSpec(fields, key);
        if (!raw?.trim()) continue;
        if (specExistsForField(key, categories, categorySpecifications)) continue;

        const value = raw.trim();
        const valueKey = value.toLowerCase();
        if (seenValues.has(valueKey)) continue;
        seenValues.add(valueKey);

        parts.push(`${label}: ${value}`);
    }

    // Age: only into description when it cannot map to a configured enum option
    const ageRaw =
        fields[BABY_AGE_RANGE_SPEC] ?? findFieldForSpec(fields, BABY_AGE_RANGE_SPEC);
    if (ageRaw?.trim() && !ageMapsToLoadedEnumOption(ageRaw, categories, categorySpecifications)) {
        const value = ageRaw.trim();
        const valueKey = `age:${value.toLowerCase()}`;
        if (!seenValues.has(valueKey)) {
            seenValues.add(valueKey);
            parts.push(`Age: ${value}`);
        }
    }

    return parts.join(' · ');
}

/** Compress image for OCR (Rekognition 5 MB limit). */
export async function compressImageForOcr(file: File, maxEdge = 1600, quality = 0.85): Promise<File> {
    if (!file.type.startsWith('image/')) return file;
    if (file.size <= 900 * 1024 && file.size < MAX_TAG_BYTES) return file;

    const bitmap = await createImageBitmap(file);
    try {
        const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
        const w = Math.max(1, Math.round(bitmap.width * scale));
        const h = Math.max(1, Math.round(bitmap.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(bitmap, 0, 0, w, h);

        const blob: Blob | null = await new Promise((resolve) =>
            canvas.toBlob((b) => resolve(b), 'image/jpeg', quality)
        );
        if (!blob) return file;
        return new File([blob], file.name.replace(/\.\w+$/, '.jpg'), {
            type: 'image/jpeg',
            lastModified: Date.now(),
        });
    } finally {
        bitmap.close();
    }
}
